import { EditOutlined } from '@ant-design/icons'
import { Avatar, Button, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { HTMLAttributes, TdHTMLAttributes } from 'react'
import CanceledIcon from 'screens/Rostering/RotaBuilder/common/components/icons/CanceledIcon'
import DoneIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DoneIcon'
import InProgressIcon from 'screens/Rostering/RotaBuilder/common/components/icons/InProgressIcon'
import OverdueIcon from 'screens/Rostering/RotaBuilder/common/components/icons/OverdueIcon'
import WarningPopover from 'screens/Rostering/RotaBuilder/common/components/popovers/WarningPopover'
import styled from 'styled-components'
import {
  type ChargeEventStatus,
  ChargeReconciliationChargeBy,
  type ChargeReconciliationRow
} from '../../../Reports/types/chargeReconciliation'
import { getDateTimeInZone, TIME_FORMAT } from '../../utils.datetime'
import { formatMinutes } from './chargeRatesUtils'
import CancelledEventPopover from './popovers/CancelledEventPopover'
import TeamMembersPopover from './popovers/TeamMembersPopover'

export type GetChargeRatesTableColumnsParams = {
  t: (key: string, options?: Record<string, unknown>) => string
  nominative: string
  onDontCharge: (record: ChargeReconciliationRow) => void
  onEditChargeAmount: (record: ChargeReconciliationRow) => void
}

const EVENT_STATUS_ICON: Record<ChargeEventStatus, React.FC> = {
  DONE: DoneIcon,
  MISSED: OverdueIcon,
  CANCELLED: CanceledIcon,
  ONGOING: InProgressIcon,
  // Overdue reuses the missed icon until a dedicated design exists.
  OVERDUE: OverdueIcon
}

const EVENT_STATUS_LABEL_KEY: Record<ChargeEventStatus, string> = {
  DONE: 'RECONCILIATION_EVENT_STATUS_DONE',
  MISSED: 'RECONCILIATION_EVENT_STATUS_MISSED',
  CANCELLED: 'RECONCILIATION_EVENT_STATUS_CANCELLED',
  ONGOING: 'RECONCILIATION_EVENT_STATUS_ONGOING',
  OVERDUE: 'RECONCILIATION_EVENT_STATUS_OVERDUE'
}

export function getChargeRatesTableColumns({
  t,
  nominative,
  onDontCharge,
  onEditChargeAmount
}: GetChargeRatesTableColumnsParams): ColumnsType<ChargeReconciliationRow> {
  const columns: ColumnsType<ChargeReconciliationRow> = [
    {
      title: (
        <LeftmostCellContainer>
          <Text weight="medium">{t('RECONCILIATION_TABLE_STATUS')}</Text>
        </LeftmostCellContainer>
      ),
      key: 'status',
      sorter: true,
      width: 148,
      render: (_, record) => {
        // Show the reconciliation status for every event (incl. cancelled), so a
        // reconciled cancelled event reads "Reconciled" rather than staying stuck
        // on "Cancelled". The event's cancelled state still shows via its event
        // status column, icon and charged-time cell.
        const inner =
          record.status === 'reconciled' ? (
            <ChargeTableTag color="success">
              {t('RECONCILIATION_STATUS_RECONCILED')}
            </ChargeTableTag>
          ) : (
            <ChargeTableTag color="orange">
              {t('RECONCILIATION_STATUS_NEEDS_ATTENTION')}
            </ChargeTableTag>
          )
        return <LeftmostCellContainer>{inner}</LeftmostCellContainer>
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_DATE')}</Text>,
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      defaultSortOrder: 'descend',
      width: 124,
      render: (date: string) => (
        <CellText>
          <CellPrimaryText>{dayjs(date).format('DD-MM-YYYY')}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <Text weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_FUNDING_TYPE')}
        </Text>
      ),
      key: 'fundingType',
      sorter: true,
      width: 160,
      render: (_, record) => (
        <CellText>
          <CellPrimaryText>{record.fundingType?.name ?? '-'}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: <Text weight="medium">{nominative}</Text>,
      key: 'serviceUser',
      sorter: true,
      width: 200,
      render: (_, record) => {
        if (!record.serviceUser) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        return (
          <CellWithAvatar>
            <AvatarCell>
              <Avatar
                url={record.serviceUser.avatarUrl ?? undefined}
                size={22}
              />
            </AvatarCell>
            <CellPrimaryText>{record.serviceUser.name}</CellPrimaryText>
          </CellWithAvatar>
        )
      }
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_TEAM_MEMBERS')}
        </MultilineHeaderText>
      ),
      key: 'teamMembers',
      sorter: true,
      width: 110,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => (
        <TeamMembersPopover members={record.teamMembers}>
          <CountTrigger>
            <CellPrimaryText>{record.teamMembers.length}</CellPrimaryText>
          </CountTrigger>
        </TeamMembersPopover>
      )
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}</Text>
      ),
      key: 'eventType',
      sorter: true,
      width: 160,
      render: (_, record) => (
        <CellText>
          <CellPrimaryText>{record.eventType.name}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_PLANNED_TIME_DURATION')}
        </MultilineHeaderText>
      ),
      key: 'plannedTime',
      sorter: true,
      width: 140,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => (
        <TimeStack>
          <CellPrimaryText>
            {getDateTimeInZone(record.plannedTime.startTime).format(
              TIME_FORMAT
            )}
            –{getDateTimeInZone(record.plannedTime.endTime).format(TIME_FORMAT)}
          </CellPrimaryText>
          <CellSecondaryText>
            {formatMinutes(record.plannedTime.durationMinutes)}
          </CellSecondaryText>
        </TimeStack>
      )
    },
    {
      title: (
        <Text weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_EVENT_STATUS')}
        </Text>
      ),
      key: 'eventStatus',
      sorter: true,
      width: 130,
      render: (_, record) => {
        const StatusIcon = EVENT_STATUS_ICON[record.eventStatus]
        const content = (
          <EventStatusCell>
            <StatusIcon />
            <CellPrimaryText>
              {t(EVENT_STATUS_LABEL_KEY[record.eventStatus])}
            </CellPrimaryText>
          </EventStatusCell>
        )
        if (record.eventStatus === 'CANCELLED') {
          return (
            <CancelledEventPopover row={record}>
              {content}
            </CancelledEventPopover>
          )
        }
        return content
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_WARNINGS')}</Text>,
      key: 'warningCount',
      sorter: true,
      width: 80,
      render: (_, record) => {
        if (!record.warningCount) {
          return (
            <CellText>
              <CellPrimaryText>0</CellPrimaryText>
            </CellText>
          )
        }
        const w = record.rawWarnings
        const messages: string[] = w
          ? [
              ...(w.clockInEarly > 0 ? [t('CLOCK_IN_EARLY')] : []),
              ...(w.clockInLate > 0 ? [t('CLOCK_IN_LATE')] : []),
              ...(w.clockOutEarly > 0 ? [t('CLOCK_OUT_EARLY')] : []),
              ...(w.clockOutLate > 0 ? [t('CLOCK_OUT_LATE')] : []),
              ...(w.skillMismatch ? [t('SKILL_MISMATCH')] : []),
              ...(w.clockInMismatch > 0 ? [t('CLOCK_IN_MISMATCH')] : []),
              ...(w.genderMismatch ? [t('GENDER_MISMATCH')] : []),
              ...(w.clockInLocationMismatch
                ? [t('CLOCKED_IN_LOCATION_MISMATCH')]
                : []),
              ...(w.clockOutLocationMismatch
                ? [t('CLOCKED_OUT_LOCATION_MISMATCH')]
                : [])
            ]
          : []
        return (
          <WarningPopover warningsData={messages}>
            <WarningCountTrigger>
              <CellPrimaryText>{record.warningCount}</CellPrimaryText>
            </WarningCountTrigger>
          </WarningPopover>
        )
      }
    },
    {
      title: (
        <Text weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_CHARGED_TIME')}
        </Text>
      ),
      key: 'chargedTime',
      sorter: true,
      width: 130,
      render: (_, record) => {
        if (record.dontChargeReason) {
          return (
            <Popover
              placement="top"
              showArrow
              trigger="hover"
              arrow={{ pointAtCenter: true }}
              content={
                <PopoverContentContainer>
                  <PopoverTitleText level={2}>
                    {t('RECONCILIATION_DONT_CHARGE_REASON_LABEL_TEXT')}
                  </PopoverTitleText>
                  <PopoverBodyText>{record.dontChargeReason}</PopoverBodyText>
                </PopoverContentContainer>
              }
            >
              <NotChargedTrigger>
                <DangerText>
                  <Text>{t('RECONCILIATION_NOT_CHARGED')}</Text>
                </DangerText>
              </NotChargedTrigger>
            </Popover>
          )
        }
        if (record.eventStatus === 'CANCELLED') {
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_EVENT_STATUS_CANCELLED')}</Text>
              </DangerText>
            </CellText>
          )
        }
        if (record.eventStatus === 'MISSED') {
          return (
            <CellText>
              <DangerText>
                <Text>{t('MISSED')}</Text>
              </DangerText>
            </CellText>
          )
        }
        if (!record.chargedTime) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        return (
          <TimeStack>
            <CellPrimaryText>
              {getDateTimeInZone(record.chargedTime.startTime).format(
                TIME_FORMAT
              )}
              –
              {getDateTimeInZone(record.chargedTime.endTime).format(
                TIME_FORMAT
              )}
            </CellPrimaryText>
            <CellSecondaryText>
              {formatMinutes(record.chargedTime.durationMinutes)}
            </CellSecondaryText>
          </TimeStack>
        )
      }
    },
    {
      title: (
        <Text weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_CHARGE_RATE')}
        </Text>
      ),
      key: 'chargeRate',
      sorter: true,
      width: 150,
      render: (_, record) => {
        if (record.chargeRate == null) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        const unit =
          record.chargeRateType === 'HOURLY'
            ? t('RATE_TYPE_HOURLY')
            : t('RATE_TYPE_FIXED')
        return (
          <TimeStack>
            <CellPrimaryText>
              {record.chargeRate} {unit}
            </CellPrimaryText>
            {record.isCustomInstanceChargeRate && (
              <CellSecondaryText>
                {t('RECONCILIATION_CUSTOM_CHARGE_RATE')}
              </CellSecondaryText>
            )}
          </TimeStack>
        )
      }
    },
    {
      title: (
        <Text weight="medium">
          {t('RECONCILIATION_CHARGE_TABLE_CHARGE_AMOUNT')}
        </Text>
      ),
      key: 'chargeAmount',
      sorter: true,
      width: 168,
      onCell: () =>
        ({
          'data-testid': 'reconciliation-charge-amount-cell'
        }) as HTMLAttributes<HTMLTableCellElement> &
          TdHTMLAttributes<HTMLTableCellElement>,
      render: (_, record) => {
        const editButton = (
          <EditIconButton
            data-testid="edit-charge-amount-btn"
            onClick={(e) => {
              e.stopPropagation()
              onEditChargeAmount(record)
            }}
          >
            <EditOutlined />
          </EditIconButton>
        )
        const isCustomAmount =
          record.chargeBy === ChargeReconciliationChargeBy.CUSTOM_AMOUNT
        if (record.chargeAmount == null) {
          return (
            <ChargeAmountCell>
              {editButton}
              <CellPrimaryText>-</CellPrimaryText>
            </ChargeAmountCell>
          )
        }
        return (
          <ChargeAmountCell>
            {editButton}
            <TimeStack>
              <CellPrimaryText>
                {record.chargeAmount} {t('RECONCILIATION_GBP_SUFFIX')}
              </CellPrimaryText>
              {isCustomAmount && (
                <CellSecondaryText>
                  {t('RECONCILIATION_CUSTOM_CHARGE_AMOUNT')}
                </CellSecondaryText>
              )}
            </TimeStack>
          </ChargeAmountCell>
        )
      }
    },
    {
      title: '',
      key: 'dontCharge',
      width: 120,
      render: (_, record) => {
        if (record.dontChargeReason) {
          return null
        }
        return (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onDontCharge(record)
            }}
          >
            {t('RECONCILIATION_CHARGE_TYPE_DONT_CHARGE')}
          </Button>
        )
      }
    }
  ]

  return columns
}

const MultilineHeaderText = styled(Text)`
  white-space: pre-line;
`

const ChargeTableTag = styled(Tag)`
  padding: 4px 8px;
  font-size: 12px;
`

const LeftmostCellContainer = styled.div`
  padding-left: 12px;
`

const CellText = styled.div``

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellSecondaryText = styled(Text).attrs({
  level: 2,
  weight: 'medium'
})`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const AvatarCell = styled.div`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-avatar {
    width: 22px !important;
    height: 22px !important;
    line-height: 22px !important;
    flex-shrink: 0;
  }

  .ant-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TimeStack = styled.div`
  display: flex;
  flex-direction: column;
`

const DangerText = styled.span`
  color: #f5222d;
`

const WarningCountTrigger = styled.span`
  cursor: pointer;
`

const CountTrigger = styled.span`
  cursor: pointer;
`

const NotChargedTrigger = styled.span`
  cursor: pointer;
`

const EventStatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
`

const ChargeAmountCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EditIconButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 16px;
  transition: color 0.15s ease;

  &:hover {
    color: #5b8def;
  }

  &:active {
    color: #2f6ad0;
  }
`

const PopoverContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 280px;
`

const PopoverTitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const PopoverBodyText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
